<template>
  <div>
    <div v-if="!consentGiven" :class="$style.consent">
      <h2>{{ storeTranslations.translations?.['consentHeading'] || fallbackConsentHeading }}</h2>
      <div v-html="vendorConsentText" />
      <Button1XP
        :class="$style.consentButton"
        :text="consentButtonLabel"
        @click="handleContentButton"
      />
    </div>
    <template v-else-if="consentGiven">
      <div :class="$style.container">
        <Loading v-if="initializing" />
        <div v-if="!initializing && !hideFilterPanel" :class="$style.filterContainer">
          <MapFilter :groups="internalFilterGroups" @change="handleFilterChange" />
        </div>
        <div
          v-if="!initializing"
          :class="[$style.contentContainer, { MapFilterApp__noFilter: hideFilterPanel }]"
        >
          <!-- <div :class="$style.intro" v-html="state.introText" /> -->
          <div ref="mapContainer" :class="$style.mapContainer">
            <Loading v-if="loading" />
            <MapVue3
              :filter-key-value="config.filterKeyValue"
              :job-offer-json-url="config.jobOfferJSONURL"
              :legend-items="state.legend"
              :map-zoom="initialZoom"
              :markers="internalMarkers"
              :use-form="config.useForm"
              @display-details="displayDetails"
              @loading-change="handleLoadingChange"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, onUpdated, ref, computed } from 'vue';
import { getVendorConsent, registerConsentListeners } from '../../globals/consentmanager';
import cmpVendorIDs from '../../globals/constants/cmpVendorIDs';
import Button1XP from '../Button1XP/Button1XP.vue';
import Loading from '../Loading/Loading.vue';
import MapFilter from '../MapFilter/MapFilter.vue';
import MapVue3 from '../MapVue3/MapVue3.vue';
import { useStoreTranslations } from '../../store/translations/translations';

const storeTranslations = useStoreTranslations();
const dictionary = window.dictionary;

const vendor = cmpVendorIDs.GOOGLEMAPS;
const vendorConsentText = computed(
  () =>
    storeTranslations.translations?.['consentText']
      ?.replaceAll('###VENDOR_NAME###', vendor.name)
      .replaceAll('#<VENDOR_NAME>#', vendor.name) || fallbackConsentText,
);
const consentButtonLabel = computed(
  () =>
    storeTranslations.translations?.['consentButtonLabel']?.replaceAll(/&apos;/g, "'") ||
    fallbackConsentButtonLabel,
);

const fallbackConsentHeading = computed(() => dictionary?.consentHeading);
const fallbackConsentText = dictionary?.consentText
  ?.replaceAll('###VENDOR_NAME###', vendor.name)
  .replaceAll('#<VENDOR_NAME>#', vendor.name);
const fallbackConsentButtonLabel = dictionary?.consentButtonLabel?.replaceAll(/&apos;/g, "'");

/*
 * * props
 */

const props = defineProps({
  state: {
    type: Object,
    default: () => {},
  },
  config: {
    type: Object,
    default: () => {},
  },
});

const checkVendorConsent = () => {
  consentGiven.value = getVendorConsent(vendor.id);
  checkConsent();
};

const handleContentButton = () => {
  if (window.cmpConsentType === 'consentmanager') {
    window.__cmp &&
      window.__cmp('setVendorConsent', [vendor.id, 1], () => {
        consentGiven.value = getVendorConsent(vendor.id);
        checkConsent();
      });
  } else {
    if (window.Cookiebot) {
      window.Cookiebot.onaccept = () => {
        consentGiven.value = true;
      };
    }
    checkConsent();
  }
};

const checkConsent = () => {
  if (consentGiven.value) {
    init();
  }
};

/*
 * * emits
 */

const emit = defineEmits(['displayDetails']);

/*
 * * refs
 */

const consentGiven = ref(window.Cookiebot ? true : getVendorConsent(vendor.id));
const initializing = ref(true);
const initialZoom = ref(props.state.initialZoom);
const internalMarkers = ref([]);
const internalFilterGroups = ref([]);
const loading = ref(true);
const mapContainer = ref(null);
const hideFilterPanel = ref(
  props.config.noFilter || !(props.state.filterGroups && props.state.filterGroups.length),
);

/*
 * * methods
 */

const handleLoadingChange = (value) => {
  loading.value = value;
};

const handleFilterChange = (filterGroupId, itemId, itemActive) => {
  const foundGroup = internalFilterGroups.value.filter(
    (filterGroup) => filterGroup.id === filterGroupId,
  )[0];

  const foundFilter = foundGroup.items.filter((item) => item.id === itemId)[0];

  foundFilter.active = itemActive;

  setInternalMarker();

  if (window.matchMedia('(max-width: 767px)').matches) {
    mapContainer.value.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }
};

const setInternalMarker = () => {
  const markers = props.state.contacts.filter((marker) => {
    let foundInGroups = 0;
    internalFilterGroups.value.forEach((filterGroup) => {
      let foundInGroup = 0;
      const markerFilterGroup = marker.filterGroups[filterGroup.id];
      filterGroup.items.forEach((f) => {
        if (f.active) {
          if (markerFilterGroup.indexOf(f.id) !== -1) {
            foundInGroup++;
          }
        }
      });

      foundInGroup > 0 && foundInGroups++;
    });

    return foundInGroups === internalFilterGroups.value.length;
  });

  internalMarkers.value = markers;
};

const setAllFilterActive = () => {
  internalFilterGroups.value = props.state.filterGroups.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      active: true,
    })),
  }));
};

const init = () => {
  if (!hideFilterPanel.value) {
    setAllFilterActive();
  }
  setInternalMarker();
  initializing.value = false;
};

const displayDetails = (url) => {
  emit('displayDetails', url);
};

/*
 * * lifecycle methods
 */

const registerConsentListenersWithDelay = (checkVendorConsent) => {
  const intervalId = setInterval(() => {
    if (typeof window.__cmp === 'function') {
      registerConsentListeners(checkVendorConsent);
      clearInterval(intervalId);
    }
  }, 100);
  setTimeout(() => clearInterval(intervalId), 5000);
};

onMounted(() => {
  if (window.cmpConsentType === 'consentmanager') {
    registerConsentListenersWithDelay(checkVendorConsent);
    setTimeout(() => {
      checkVendorConsent();
    }, 1000);
  } else {
    checkConsent();
  }
});

onUpdated(() => {
  if (!consentGiven.value) {
    if (window.cmpConsentType === 'consentmanager') {
      registerConsentListeners(checkVendorConsent);
      checkVendorConsent();
    } else {
      checkConsent();
    }

    if (window.Cookiebot) {
      consentGiven.value = true;
    } else {
      consentGiven.value = getVendorConsent(vendor.id);
    }
  }
});
</script>

<style module lang="scss">
@forward './MapFilterApp.scss';
</style>
